import React, { useEffect, useRef, useState } from 'react';
import {
  FiUpload, FiVideo, FiMonitor, FiBookOpen, FiFile, FiX, FiSend, FiFileText, FiCheckSquare, FiLink, FiArrowRight, FiPlus,
} from 'react-icons/fi';
import { MdOutlineCampaign } from 'react-icons/md';
import { DateTime } from 'luxon';
import { TYPE_LABELS, typeOptions, formatBytes } from './quickAddFiles';
import { totalWeeks, ZONE } from './instructorHome';
import { studentPreview } from '../../courses/courseDetail/courseContentMain/contentActionRules';
import { normalizeContentLink } from '../../../pages/admin/content/addContentModal/addContentHelpers';
import { afterClassToday } from '../../../pages/admin/content/addContentModal/useAddContentModal/publishTiming';
import styles from './InstructorHome.module.css';

const TYPE_ICONS = { recording: FiVideo, slide: FiMonitor, reading: FiBookOpen, syllabus: FiFileText };

function timingValue(t) {
  if (!t || t.publishMode === 'now') return 'now';
  if (t.publishMode === 'afterClass') return 'afterClass';
  if (t.publishMode === 'tomorrow') return 'tomorrow';
  return 'later';
}

function WhenControl({ timing, setTiming, course, disabled }) {
  const after = afterClassToday(course);
  const value = timingValue(timing);
  const onChange = (e) => {
    const v = e.target.value;
    const now = DateTime.now().setZone(ZONE);
    if (v === 'now') setTiming({ publishMode: 'now', publishDate: '', publishTime: '' });
    else if (v === 'afterClass' && after) setTiming({ publishMode: 'afterClass', publishDate: after.toISODate(), publishTime: after.toFormat('HH:mm') });
    // "tomorrow" is just a preset date; it posts as a scheduled ("later") time.
    else if (v === 'tomorrow') setTiming({ publishMode: 'tomorrow', publishDate: now.plus({ days: 1 }).toISODate(), publishTime: '08:00' });
    else setTiming({ publishMode: 'later', publishDate: timing.publishDate || now.plus({ days: 1 }).toISODate(), publishTime: timing.publishTime || '08:00' });
  };
  return (
    <div className={styles.when}>
      <label htmlFor="qa-when">Students see it:</label>
      <select id="qa-when" className={`${styles.select} ${styles.whenSelect}`} value={value} onChange={onChange} disabled={disabled}>
        <option value="now">Now</option>
        {after && <option value="afterClass">After class · {after.toFormat('h:mm a')}</option>}
        <option value="tomorrow">Tomorrow · 8:00 AM</option>
        <option value="later">On a date…</option>
      </select>
      {value === 'later' && (
        <span className={styles.whenFields}>
          <input
            type="date"
            aria-label="Date students see it"
            className={styles.whenInput}
            value={timing.publishDate || ''}
            min={DateTime.now().setZone(ZONE).toISODate()}
            onChange={(e) => setTiming({ ...timing, publishDate: e.target.value })}
            disabled={disabled}
          />
          <input
            type="time"
            aria-label="Time students see it (CT)"
            className={styles.whenInput}
            value={timing.publishTime || '08:00'}
            onChange={(e) => setTiming({ ...timing, publishTime: e.target.value })}
            disabled={disabled}
          />
        </span>
      )}
    </div>
  );
}

function StagedItem({ item, weeks, onChange, onRemove, busy }) {
  const Icon = TYPE_ICONS[item.type] || FiFile;
  const opts = item.linkOnly ? ['slide', 'reading', 'syllabus'] : typeOptions(item.file);
  const editable = !busy && (item.status === 'ready' || item.status === 'error');
  const isDoc = item.type && item.type !== 'recording';
  const preview = isDoc && item.status !== 'unsupported' ? studentPreview({ link: normalizeContentLink(item.url), fileName: item.linkOnly ? '' : item.name }) : null;
  const [linkOpen, setLinkOpen] = useState(false);
  const showLinkInput = item.linkOnly || linkOpen || Boolean(item.url);
  const sub = item.linkOnly ? 'Link only' : item.status === 'uploading'
    ? `${formatBytes(item.size)} · ${item.percent >= 100 ? 'Upload received · finishing up…' : `Uploading ${item.percent || 0}%`}`
    : formatBytes(item.size);
  return (
    <div className={styles.item}>
      <span className={styles.itemIcon} aria-hidden="true"><Icon size={17} /></span>
      <div className={styles.itemMain}>
        {item.status === 'unsupported' ? (
          <span className={styles.itemName}>{item.name}</span>
        ) : (
          <input
            className={styles.itemTitle}
            value={item.title}
            maxLength={255}
            onChange={(e) => onChange(item.key, { title: e.target.value })}
            aria-label={`Title for ${item.name}`}
            placeholder={item.linkOnly ? 'Title' : undefined}
            disabled={!editable}
          />
        )}
        <span className={styles.itemMeta}>{item.status === 'unsupported' || item.linkOnly ? '' : `${item.name} · `}{sub}</span>
        {item.status === 'uploading' && (
          <span className={styles.bar} role="progressbar" aria-valuenow={item.percent || 0} aria-valuemin={0} aria-valuemax={100} aria-label={`Uploading ${item.name}`}>
            <span className={styles.barFill} style={{ width: `${item.percent || 0}%` }} />
          </span>
        )}
        {preview && editable && !showLinkInput && (
          <button type="button" className={styles.itemAddLink} onClick={() => setLinkOpen(true)}>+ Add a link</button>
        )}
        {preview && editable && showLinkInput && (
          <input
            className={styles.itemLink}
            value={item.url || ''}
            maxLength={500}
            inputMode="url"
            autoComplete="off"
            placeholder="https://example.com"
            onChange={(e) => onChange(item.key, { url: e.target.value })}
            aria-label={`Link for ${item.name}`}
          />
        )}
        {preview && (
          <>
            <span
              className={preview.warning ? styles.itemWarn : styles.itemMeta}
              data-testid="quick-add-preview"
              role={preview.problem ? 'status' : undefined}
            >
              {preview.problem || preview.warning || `Students get: ${preview.buttons.join(' + ')}`}
            </span>
            {preview.sharing && <span className={styles.itemMeta}>{preview.sharing}</span>}
          </>
        )}
        {item.error && !(preview?.problem && item.error !== 'Add a title.') && <span className={styles.itemError}>{item.error}</span>}
      </div>
      <span className={styles.itemSelects}>
        {opts.length > 1 ? (
          <select
            className={`${styles.select} ${styles.selectSm}`}
            value={item.type}
            onChange={(e) => onChange(item.key, { type: e.target.value, ...(e.target.value === 'syllabus' ? { week: 0 } : {}) })}
            aria-label={`Type for ${item.name}`}
            disabled={!editable}
          >
            {opts.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
          </select>
        ) : (
          <span className={styles.fixedType}>{item.type ? TYPE_LABELS[item.type] : '—'}</span>
        )}
        {item.type === 'syllabus' ? <span className={styles.fixedType}>Course-wide</span> : item.type ? (
          <select
            className={`${styles.select} ${styles.selectSm}`}
            value={item.week}
            onChange={(e) => onChange(item.key, { week: Number(e.target.value) })}
            aria-label={`Week for ${item.name}`}
            disabled={!editable}
          >
            <option value={0}>General</option>
            {weeks.map((w) => <option key={w} value={w}>Week {w}</option>)}
          </select>
        ) : <span />}
      </span>
      <button type="button" className={styles.iconBtn} onClick={() => onRemove(item.key)} aria-label={`Remove ${item.name}`} disabled={item.status === 'uploading'}>
        <FiX size={16} />
      </button>
    </div>
  );
}

/** Q2: Quick Add on the instructor dashboard. */
export default function QuickAdd({ quick, courses, onNewAssignment, onNewQuiz, onAllAssignments, onAllQuizzes, onManage }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const {
    course, courseId, chooseCourse, week, chooseWeek, items, addFiles, addLink, updateItem, removeItem, clear,
    timing, setTiming, sendEmail, setSendEmail, posting, post, postableCount, message,
  } = quick;

  const [takingLonger, setTakingLonger] = useState(false);
  useEffect(() => {
    setTakingLonger(false);
    if (!posting) return;
    const timer = setTimeout(() => setTakingLonger(true), 20000);
    return () => clearTimeout(timer);
  }, [posting]);
  const activeIndex = items.findIndex(item => item.status === 'uploading');
  const uploading = items[activeIndex];

  if (!courses.length) return null;
  const weeks = course ? Array.from({ length: totalWeeks(course) }, (_, i) => i + 1) : [1];
  const pickFiles = () => inputRef.current?.click();

  const ActionRow = ({ icon: Icon, title, sub, onClick }) => (
    <button type="button" className={styles.actionRow} onClick={onClick}>
      <span className={styles.itemIcon} aria-hidden="true"><Icon size={16} /></span>
      <span className={styles.actionText}>
        <span className={styles.actionTitle}>{title}</span>
        <span className={styles.actionSub}>{sub}</span>
      </span>
      <FiArrowRight size={15} className={styles.actionArrow} aria-hidden="true" />
    </button>
  );

  return (
    <section className={styles.section} aria-labelledby="quick-add-title">
      <div className={styles.sectionHead}>
        <h2 id="quick-add-title" className={styles.sectionTitle}>Quick Add</h2>
        <div className={styles.quickAddTools}>
          {courses.length > 1 ? (
            <select className={styles.select} value={courseId || ''} onChange={(e) => chooseCourse(Number(e.target.value))} aria-label="Course" disabled={posting}>
              {courses.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
            </select>
          ) : null}
        </div>
      </div>

      {/* U1: three cards the same size as the lanes and course cards */}
      <div className={styles.grid3}>
        <div
          className={`${styles.uCard} ${styles.toneDrop}`}
          onDragOver={(e) => { e.preventDefault(); if (!over) setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); if (!posting) addFiles(e.dataTransfer.files); }}
        >
          <div className={styles.laneHead}>
            <span className={styles.laneIcon} aria-hidden="true"><FiUpload size={17} /></span>
            <h3 className={styles.laneTitle}>Drop files</h3>
            <select className={`${styles.select} ${styles.dropWeek}`} value={week} onChange={(e) => chooseWeek(Number(e.target.value))} aria-label="Week" disabled={posting}>
              <option value={0}>General</option>
              {weeks.map((w) => <option key={w} value={w}>Week {w}</option>)}
            </select>
          </div>
          <div className={styles.dropFill}>
            <div className={`${styles.dropArea} ${over ? styles.dropOver : ''}`}>
              <span className={styles.dropHint}>Drop your files here then choose whether each is a recording, slide, reading, or syllabus.</span>
              <span className={styles.dropButtons}>
                <button type="button" className={styles.btn} onClick={pickFiles} disabled={posting}>
                  <FiUpload size={15} aria-hidden="true" />Choose files
                </button>
                <button type="button" className={styles.btn} onClick={addLink} disabled={posting}>
                  <FiLink size={15} aria-hidden="true" />Add a link
                </button>
              </span>
            </div>
          </div>
          <input
            ref={inputRef}
            type="file"
            multiple
            className={styles.hiddenInput}
            tabIndex={-1}
            aria-hidden="true"
            accept="video/mp4,.mp4,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.html,.htm"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
          />
        </div>

        <div className={`${styles.uCard} ${styles.toneExt}`}>
          <div className={styles.laneHead}>
            <span className={styles.laneIcon} aria-hidden="true"><MdOutlineCampaign size={18} /></span>
            <h3 className={styles.laneTitle}>Post something else</h3>
          </div>
          <div className={styles.actionList}>
            <ActionRow icon={MdOutlineCampaign} title="Announcement" sub="Post and email students" onClick={() => onManage(course, 'announcements')} />
            <ActionRow icon={FiLink} title="Link" sub="A reading or video link" onClick={() => onManage(course, 'readings')} />
            <ActionRow icon={FiFile} title="Syllabus" sub="Course-wide" onClick={() => onManage(course, 'syllabus')} />
          </div>
          <div className={styles.laneFoot}>
            <button type="button" className={styles.linkBtn} onClick={() => onManage(course)}>Manage content <FiArrowRight size={13} aria-hidden="true" /></button>
          </div>
        </div>

        <div className={`${styles.uCard} ${styles.toneCreate}`}>
          <div className={styles.laneHead}>
            <span className={styles.laneIcon} aria-hidden="true"><FiPlus size={17} /></span>
            <h3 className={styles.laneTitle}>Create</h3>
          </div>
          <div className={styles.actionList}>
            <ActionRow icon={FiFileText} title="New assignment" sub="With a due date" onClick={onNewAssignment} />
            <ActionRow icon={FiCheckSquare} title="New quiz/exam" sub="From your library or new" onClick={onNewQuiz} />
          </div>
          <div className={`${styles.laneFoot} ${styles.createLinks}`}>
            <button type="button" className={styles.linkBtn} onClick={onAllAssignments}>All assignments <FiArrowRight size={13} aria-hidden="true" /></button>
            <button type="button" className={styles.linkBtn} onClick={onAllQuizzes}>All quizzes/exams <FiArrowRight size={13} aria-hidden="true" /></button>
          </div>
        </div>
      </div>

      {posting && (
        <div className={`${styles.note} ${styles.noteProgress}`} role="status" aria-live="polite" aria-atomic="true">
          <strong>{uploading
            ? uploading.percent >= 100
              ? `Finishing ${uploading.name}…`
              : `Uploading ${uploading.name} (${uploading.percent || 0}%)…`
            : 'Preparing your upload…'}</strong>
          <span>{uploading ? `File ${activeIndex + 1} of ${items.length}. ` : ''}Please keep this page open until confirmation appears.</span>
          {takingLonger && <span>{uploading?.percent >= 100
            ? 'Your file has been received. Saving it may take a little longer.'
            : 'Your upload is still in progress. Large files or slower connections can take longer.'}</span>}
        </div>
      )}
      {message && (
        <p className={`${styles.note} ${message.tone === 'ok' ? styles.noteOk : styles.noteErr}`} role="status">{message.text}</p>
      )}

      {items.length > 0 && (
        <div className={`${styles.tray} ${styles.trayCard}`} aria-busy={posting}>
          <div className={styles.trayHead}>
            <h3 className={styles.trayTitle}>Ready to post</h3>
            <span className={styles.pill}>{items.length}</span>
            <WhenControl timing={timing} setTiming={setTiming} course={course} disabled={posting} />
          </div>
          {items.map((item) => (
            <StagedItem key={item.key} item={item} weeks={weeks} onChange={updateItem} onRemove={removeItem} busy={posting} />
          ))}
          <div className={styles.trayFoot}>
            <label className={styles.toggle}>
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} disabled={posting} />
              <span className={`${styles.switch} ${sendEmail ? styles.switchOn : ''}`} aria-hidden="true" />
              Email students{timing.publishMode !== 'now' && sendEmail ? ' when it goes live' : ''}
            </label>
            <div className={styles.footActions}>
              <button type="button" className={styles.btn} onClick={clear}>{posting ? 'Stop' : 'Clear'}</button>
              <button type="button" className={`${styles.btn} ${styles.btnGo}`} onClick={post} disabled={posting || postableCount === 0}>
                <FiSend size={14} aria-hidden="true" />
                {posting ? 'Posting…' : `Post ${postableCount}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
